import type {
  CandidateComparison,
  ComparisonFlag,
  HaynesProCandidate,
  HaynesProLookupComparison,
} from '@/hooks/useVehicleTechnicalData';

export const KNOWN_TECHNICAL_FIELDS = ['engineCode', 'capacity', 'output'] as const;

export type KnownTechnicalField = (typeof KNOWN_TECHNICAL_FIELDS)[number];

export interface KnownTechnicalSnapshot {
  engineCode: string | null;
  capacity: number | null;
  output: number | null;
}

export interface PreferredCandidatePartition {
  preferred: HaynesProCandidate[];
  others: HaynesProCandidate[];
  hasKnownTechnicalFields: boolean;
}

function isKnownEngineCode(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function isKnownTechnicalNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function isKnownTechnicalField(
  comparison: HaynesProLookupComparison,
  field: KnownTechnicalField,
): boolean {
  if (field === 'engineCode') return isKnownEngineCode(comparison.engineCode);
  return isKnownTechnicalNumber(comparison[field]);
}

export function knownTechnicalSnapshot(
  comparison: HaynesProLookupComparison,
): KnownTechnicalSnapshot {
  return {
    engineCode: isKnownEngineCode(comparison.engineCode) ? comparison.engineCode.trim() : null,
    capacity: isKnownTechnicalNumber(comparison.capacity) ? comparison.capacity : null,
    output: isKnownTechnicalNumber(comparison.output) ? comparison.output : null,
  };
}

export function hasKnownTechnicalFields(comparison: HaynesProLookupComparison): boolean {
  return KNOWN_TECHNICAL_FIELDS.some((field) => isKnownTechnicalField(comparison, field));
}

function knownTechnicalFields(comparison: HaynesProLookupComparison): KnownTechnicalField[] {
  return KNOWN_TECHNICAL_FIELDS.filter((field) => isKnownTechnicalField(comparison, field));
}

export function hasExplicitIdentityMismatch(comparison: CandidateComparison): boolean {
  return comparison.make === 'mismatch' || comparison.model === 'mismatch' || comparison.year === 'mismatch';
}

const MATCHED: ComparisonFlag = 'matched';

export function matchesAllKnownTechnicalFields(
  candidate: HaynesProCandidate,
  comparison: HaynesProLookupComparison,
): boolean {
  const known = knownTechnicalFields(comparison);
  if (known.length === 0) return false;
  return known.every((field) => candidate.comparison[field] === MATCHED);
}

export function isPreferredKnownTechnicalCandidate(
  candidate: HaynesProCandidate,
  comparison: HaynesProLookupComparison,
): boolean {
  return (
    matchesAllKnownTechnicalFields(candidate, comparison) &&
    !hasExplicitIdentityMismatch(candidate.comparison)
  );
}

export function partitionPreferredCandidates(
  candidates: readonly HaynesProCandidate[],
  comparison: HaynesProLookupComparison,
): PreferredCandidatePartition {
  if (!hasKnownTechnicalFields(comparison)) {
    return {
      preferred: [],
      others: candidates.slice(),
      hasKnownTechnicalFields: false,
    };
  }

  const preferred: HaynesProCandidate[] = [];
  const others: HaynesProCandidate[] = [];
  for (const candidate of candidates) {
    if (isPreferredKnownTechnicalCandidate(candidate, comparison)) {
      preferred.push(candidate);
    } else {
      others.push(candidate);
    }
  }
  return { preferred, others, hasKnownTechnicalFields: true };
}

export function candidateHaystack(candidate: HaynesProCandidate): string {
  return [
    candidate.name,
    candidate.fullName,
    candidate.engineCode,
    candidate.fuelType,
    candidate.carTypeId,
    candidate.madeFrom,
    candidate.madeUntil,
  ]
    .filter((value) => value !== null && value !== undefined)
    .join(' ')
    .toLowerCase();
}

export function filterCandidates(
  candidates: readonly HaynesProCandidate[],
  query: string,
): HaynesProCandidate[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return candidates.slice();
  return candidates.filter((item) => candidateHaystack(item).includes(needle));
}

export function visibleHaynesProCandidates(
  candidates: readonly HaynesProCandidate[],
  comparison: HaynesProLookupComparison,
  options: { showOthers: boolean; filterQuery: string },
): HaynesProCandidate[] {
  const { preferred, others } = partitionPreferredCandidates(candidates, comparison);
  const base =
    preferred.length > 0 && !options.showOthers ? preferred : [...preferred, ...others];
  return filterCandidates(base, options.filterQuery);
}
